import MantineTable from 'components/MantineTable';
import { Box, Button, Checkbox, Group, Image, Select, Stack } from '@mantine/core';
import { TableContext } from 'hooks/tables/TableContext';
import { useContext, useEffect, useState } from 'react';
import { Column } from 'components/MantineTable/MantineTable';
import { useFetchAndLoad } from 'hooks';
import { useDisclosure } from '@mantine/hooks';
import { MantineDeleteModal } from '@/components/MantineDeleteModal/MantineDeleteModal';
import { AdminTableRow } from '@/interfaces/admin.interface';
import { remove } from '@/services/admin.service';
import displayToast from 'utilities/toast.utility';
import LogoBonum from 'assets/images/logo.png';
import { runTableExport } from 'utilities/shared/runTableExport';
import TableActions from '@/components/MantineTable/components/TableActions';

interface IAdminRowIndex extends AdminTableRow {
  index: number;
}

enum RoleType {
  admin = 'admin',
  superadmin = 'superadmin',
  user = 'user',
}

export const AdminTable = () => {
  const [loader, setLoader] = useState(false);
  const [multiSelectKey, setMultiSelectKey] = useState(Date.now());

  const { callEndpoint } = useFetchAndLoad();

  const {
    form,
    loading,
    handleExportAllData,
    refetch,
    showAdvanceSearch,
    setShowAdvanceSearch,
    cleanFilters,
    handleSubmit,
  } = useContext(TableContext);

  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminTableRow | null>(null);

  const handleView = (id: string) => {
    window.open(`/admins/view/${id}`);
  };

  const handleEdit = (id: string) => {
    window.open(`/admins/edit/${id}`);
  };

  const handleDelete = async () => {
    try {
      setLoader(true);
      if (!selectedAdmin?.id) {
        displayToast('Ocurrió un error al eliminar Admin, inténtelo nuevamente', 'error');
        return false;
      }
      await callEndpoint(remove(selectedAdmin?.id));
      displayToast('Admin eliminado correctamente.', 'success');
      refetch();
      return true;
    } catch (error) {
      console.error('Ocurrió un error al eliminar Admin:', error);
      displayToast('Ocurrió un error al eliminar Admin', 'error');
      return false;
    } finally {
      closeDeleteModal();
      setSelectedAdmin(null);
      setLoader(false);
    }
  };

  const handleOpenDeletedModal = ({ row }: { row: AdminTableRow; index: number }) => {
    setSelectedAdmin(row);
    openDeleteModal();
  };

  const handleCleanUp = () => {
    setMultiSelectKey(Date.now());
    cleanFilters();
  };

  const columns: Column<IAdminRowIndex>[] = [
    {
      accessor: 'selected',
      label: 'Seleccionar',
      render: (_value, _row, index) => (
        <Checkbox
          checked={form?.values?.data[index]?.selected ?? false}
          onChange={(event) => {
            if (!form?.values?.data[index]) return;
            form.setFieldValue(`data.${index}.selected`, event.currentTarget.checked);
          }}
        />
      ),
    },
    {
      accessor: 'name',
      label: 'Nombre',
    },
    {
      accessor: 'email',
      label: 'Email',
    },
    {
      accessor: 'role',
      label: 'Rol',
    },
    {
      accessor: 'photo',
      label: 'Imagen',
      render: (value) => (
        <Image
          src={value || LogoBonum}
          alt="Admin photo"
          w={40}
          h={40}
          radius="md"
          fallbackSrc={LogoBonum}
        />
      ),
    },
    {
      accessor: 'actions',
      label: 'Acciones',
      render: (_value, row, index) => (
        <TableActions
          row={row}
          viewAction={() => handleView(row.id)}
          editAction={() => handleEdit(row.id)}
          deleteAction={() => handleOpenDeletedModal({ row, index })}
        />
      ),
    },
  ];

  useEffect(() => {
    setMultiSelectKey(Date.now());
  }, [form?.values?.filters]);

  const handleExport = async (all: boolean = true) => {
    setLoader(true);
    try {
      const exportColumns = [
        { title: 'Nombre', key: 'name' },
        { title: 'Email', key: 'email' },
        { title: 'Rol', key: 'role' },
      ];
      await runTableExport({
        all,
        form,
        handleExportAllData,
        exportColumns,
        fileNamePrefix: 'admins',
      });
    } catch (error) {
      console.error('Error al exportar datos:', error);
      displayToast('Error al exportar datos', 'error');
    } finally {
      setLoader(false);
    }
  };

  return (
    <Box px={35} pb={50}>
      <MantineTable
        title={'Administradores'}
        form={form}
        columns={columns}
        data={[]}
        loading={loading}
        withSearch={!showAdvanceSearch}
        searchAction={
          <Button onClick={cleanFilters} color="blue.7">
            Limpiar búsqueda
          </Button>
        }
      >
        <Stack mb={'md'} mt={'lg'} gap={0}>
          <Group gap={10} justify="space-between" align="flex-end">
            <Group>
              <Button onClick={() => setShowAdvanceSearch(!showAdvanceSearch)} variant="outline">
                {showAdvanceSearch ? 'Ocultar' : 'Mostrar'} búsqueda avanzada
              </Button>
              <Button color="green.7" onClick={() => handleExport(true)}>
                Exportar todos
              </Button>
              <Button variant="outline" color="green.7" onClick={() => handleExport(false)}>
                Exportar
              </Button>
            </Group>
            <Select
              w={500}
              label="Ordenar por"
              data={[
                {
                  value: 'createdAt-desc',
                  label: 'Fecha de Creación (Más Reciente a Más Antiguo)',
                },
                {
                  value: 'createdAt-asc',
                  label: 'Fecha de Creación (Más Antiguo a Más Reciente)',
                },
                { value: 'name-asc', label: 'Nombre (A-Z)' },
                { value: 'name-desc', label: 'Nombre (Z-A)' },
                { value: 'email-asc', label: 'Email (A-Z)' },
                { value: 'email-desc', label: 'Email (Z-A)' },
                { value: 'role-asc', label: 'Rol (A-Z)' },
                { value: 'role-desc', label: 'Rol (Z-A)' },
              ]}
              {...form.getInputProps('sort')}
            />
          </Group>
          {showAdvanceSearch && (
            <Group mt={'sm'} mb={'md'} justify="space-between" align="flex-end">
              <Select
                key={`${multiSelectKey}-roles`}
                label="Filtrar por Rol"
                placeholder="Seleccionar roles"
                data={[
                  { value: RoleType.admin, label: 'Admin' },
                  { value: RoleType.superadmin, label: 'Super Admin' },
                  { value: RoleType.user, label: 'Usuario' },
                ]}
                {...form.getInputProps('filters.role')}
                clearable
                searchable
              />
              <Group flex={1} justify="flex-end" gap={10}>
                <Button onClick={() => handleSubmit(form.values)}>
                  Realizar búsqueda avanzada
                </Button>
                <Button onClick={handleCleanUp} color="blue.7">
                  Limpiar filtros
                </Button>
              </Group>
            </Group>
          )}
        </Stack>
      </MantineTable>
      <MantineDeleteModal
        opened={deleteModalOpened}
        close={closeDeleteModal}
        callBack={handleDelete}
        title="Eliminar Admin"
        label={`¿Estás seguro de que deseas eliminar al admin "${selectedAdmin?.name}"?`}
        loading={loader}
      />
    </Box>
  );
};
